/**
 * sessionTelemetry.js — Active session duration + click count.
 *
 * Design choices (all optimised for ACCURACY, not convenience):
 *
 *  1. Heartbeat is engagement-gated, not wall-clock. A heartbeat only
 *     fires when BOTH conditions hold:
 *       • `document.visibilityState === "visible"` (tab is actually open)
 *       • The user has interacted within the IDLE_WINDOW (mousemove,
 *         keydown, scroll, click or touchstart within the last 60s).
 *     This means "opened the page and walked away" contributes zero
 *     seconds — exactly what Rani asked for.
 *
 *  2. Fixed cadence of 15s per heartbeat. Server clamps `delta_s` to
 *     the [1, 30] range so a hostile client cannot inflate the duration.
 *
 *  3. `beforeunload` + `pagehide` fire ONE final heartbeat via
 *     navigator.sendBeacon so the last engaged seconds are captured
 *     even when the user closes the tab abruptly.
 *
 *  4. Click counter is throttled to AT MOST one event per second per
 *     session so a jittery mouse-double-click shows as a single click.
 *
 *  5. Everything is skipped on /admin routes (Clarity + GTM were just
 *     banned there — telemetry stays banned too to keep CRM pageviews
 *     off every tracker).
 *
 *  6. One single init call from App.js. Idempotent — safe to re-invoke
 *     after HMR or route changes.
 */
import { getSessionId, getVisitId } from "@/lib/device";

const HEARTBEAT_INTERVAL_MS = 15_000;   // every 15s while active
const HEARTBEAT_DELTA_S = 15;           // declared delta each ping (server clamps)
const IDLE_WINDOW_MS = 60_000;          // user must have interacted within 60s
const CLICK_THROTTLE_MS = 1_000;        // max 1 counted click / second

let _initialized = false;
let _heartbeatTimer = null;
let _lastInteractionMs = 0;
let _lastClickSentMs = 0;

function _isAdmin() {
  try { return typeof window !== "undefined"
    && (window.location.pathname || "").startsWith("/admin"); }
  catch { return false; }
}

function _apiBase() {
  // Match the axios instance in src/lib/api.js — avoid importing it to
  // keep the telemetry hook dependency-light and sendBeacon-friendly.
  try { return process.env.REACT_APP_BACKEND_URL || ""; } catch { return ""; }
}

function _bumpInteraction() {
  _lastInteractionMs = Date.now();
}

function _isUserActive() {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  if (_lastInteractionMs === 0) return false;
  return (Date.now() - _lastInteractionMs) < IDLE_WINDOW_MS;
}

function _postJson(path, body, useBeacon = false) {
  try {
    const url = `${_apiBase()}/api${path}`;
    const json = JSON.stringify(body);
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([json], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => { /* swallow — telemetry must never surface errors */ });
  } catch { /* ignore */ }
}

function _sendHeartbeat(useBeacon = false) {
  if (_isAdmin()) return;
  if (!_isUserActive()) return;
  const sid = getSessionId();
  if (!sid) return;
  _postJson("/track/heartbeat", {
    session_id: sid,
    visit_id: getVisitId(),
    path: window.location.pathname,
    delta_s: HEARTBEAT_DELTA_S,
  }, useBeacon);
}

function _sendClick() {
  if (_isAdmin()) return;
  const now = Date.now();
  if (now - _lastClickSentMs < CLICK_THROTTLE_MS) return;
  _lastClickSentMs = now;
  const sid = getSessionId();
  if (!sid) return;
  _postJson("/track/interaction", {
    session_id: sid,
    visit_id: getVisitId(),
    type: "click",
    path: window.location.pathname,
  }, false);
}

/**
 * Boot the telemetry hooks exactly once per page lifetime. Safe to call
 * from any React effect — subsequent calls are no-ops.
 */
export function startSessionTelemetry() {
  if (_initialized) return;
  if (typeof window === "undefined") return;
  _initialized = true;

  // Seed last-interaction so the very first visible-tab heartbeat counts.
  // A fresh pageview IS an engagement signal; without this seed we'd
  // under-count the first 60s of every session.
  _bumpInteraction();

  // Interaction detectors — passive to keep scrolling buttery.
  const bump = () => _bumpInteraction();
  window.addEventListener("mousemove", bump, { passive: true });
  window.addEventListener("keydown", bump, { passive: true });
  window.addEventListener("scroll", bump, { passive: true });
  window.addEventListener("touchstart", bump, { passive: true });

  // Click = engagement AND increments the clicks counter.
  window.addEventListener("click", () => { _bumpInteraction(); _sendClick(); }, { passive: true });

  // Fire one heartbeat 1s after boot (so a bounce visitor still records
  // a non-zero duration), then settle into the 15s cadence.
  setTimeout(() => _sendHeartbeat(false), 1000);
  _heartbeatTimer = setInterval(() => _sendHeartbeat(false), HEARTBEAT_INTERVAL_MS);

  // Flush a final heartbeat when the tab becomes hidden or is being
  // unloaded. sendBeacon guarantees delivery even on close.
  const flush = () => _sendHeartbeat(true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
    else _bumpInteraction();
  });
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", flush);
}

/** Test / HMR hook — not used in production code. */
export function _resetSessionTelemetryForTests() {
  _initialized = false;
  _lastInteractionMs = 0;
  _lastClickSentMs = 0;
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
}
